import { configuredString } from '../config/kioskRuntimeConfig';
import type {
  EntrPaymentTransport,
  PayCardInput,
  PayCardOutput,
  TransportResult,
} from '../types';
import {
  type ActiveIntentStore,
  intentMatchesInput,
  type PaymentAccountContext,
  type PersistedActiveIntent,
  resolvePaymentAccount,
  sameIntent,
  withStage,
} from './activeIntent';
import { createCaptureReconciler } from './captureReconciler';
import {
  type CaptureMode,
  hasPaymentFailure,
  isAcceptedPayment,
  isCaptureReady,
  isKnownStatus,
  isNonRecoverableFailure,
  isReaderWaitingStatus,
  isRefusedWith,
  isSameFailure,
  isTerminalFailure,
  isTerminalSuccess,
  MISSING_READER_FAILURE,
  PAYMENT_TIMEOUT_CODE,
  type PaymentFailure,
  type PaymentIntentDto,
  PaymentRefusedError,
  READER_BUSY_CODE,
  READER_OFFLINE_CODE,
  RequestOutcomeUnknownError,
  UNKNOWN_INTENT_CODE,
  UNKNOWN_READER_CODE,
} from './paymentStatus';
import {
  createReaderCancelRegistry,
  type ReaderCancelOptions,
  type ReaderCancelRegistry,
} from './readerCancelRegistry';

export type {
  ActiveIntentStage,
  ActiveIntentStore,
  PaymentAccountContext,
  PersistedActiveIntent,
} from './activeIntent';
export { createStorageActiveIntentStore } from './activeIntent';
export type {
  CaptureMode,
  PaymentFailure,
  PaymentIntentDto,
  PaymentIntentStatus,
  PaymentOutcome,
} from './paymentStatus';

export interface ReaderActionRead {
  intent: PaymentIntentDto;
  actionStatus: string | null;
  actionPaymentIntent: string | null;
}

export interface CancelReaderAnswer {
  // Only with a release of the hold: the status after the decision.
  paymentIntentStatus?: string | null;
}

export interface TerminalPaymentDeps {
  resolveReaderId(): string | null;
  resolvePaymentAccount?(): PaymentAccountContext;
  createPaymentIntent(input: {
    amount: number;
    currency: string;
    type: 'terminal';
    idempotencyKey: string;
    sessionId: string;
    partnerStripeId: string | null;
    checkoutKey?: string;
    copyAmountsCents?: readonly number[];
  }): Promise<PaymentIntentDto>;
  sendPaymentIntentToReader(input: {
    paymentIntentId: string;
    readerId: string;
    sessionId: string;
    partnerStripeId: string | null;
  }): Promise<PaymentIntentDto>;
  checkPaymentIntentStatus(input: {
    paymentIntentId: string;
    sessionId: string;
    partnerStripeId: string | null;
  }): Promise<PaymentIntentDto>;
  // The status read with the reader, after a send without an answer. Null
  // when the answer has no reader.
  readReaderAction?(input: {
    paymentIntentId: string;
    readerId: string;
    sessionId: string;
    partnerStripeId: string | null;
  }): Promise<ReaderActionRead | null>;
  capturePaymentIntent(input: {
    paymentIntentId: string;
    sessionId: string;
    partnerStripeId: string | null;
    // The capture guard compares this against the order the session was last
    // paid for; omitting it makes the backend refuse a legitimate second sale.
    checkoutKey?: string;
  }): Promise<PaymentIntentDto>;
  // The step that accepts a held payment of a declared order. The kiosk calls
  // it instead of the capture when the create answers capture: after_print.
  holdPaymentIntent(input: {
    paymentIntentId: string;
    sessionId: string;
    partnerStripeId: string | null;
    checkoutKey?: string;
  }): Promise<PaymentIntentDto>;
  cancelPaymentOnReader(
    input: PersistedActiveIntent,
    options: ReaderCancelOptions
  ): Promise<CancelReaderAnswer | undefined>;
  activeIntentStore: ActiveIntentStore;
  wait(ms: number): Promise<void>;
  // The clock of the card window. The default is Date.now.
  now?(): number;
  observer?: TerminalPaymentObserver;
}

export interface TerminalPaymentObserver {
  recordEvent(
    category: string,
    message: string,
    data?: Record<string, unknown>
  ): void;
  recordException(error: unknown, tag: string): void;
}

export interface TerminalPaymentOptions {
  pollIntervalMs?: number;
  // The time from the answer of the send in which the reader waits for a card.
  cardWindowMs?: number;
  // The time with no status answer after which the attempt ends.
  statusSilenceMs?: number;
}

type PaymentReadinessResult =
  | { kind: 'ready'; intent: PaymentIntentDto }
  | { kind: 'final-failure'; status: string; failure?: PaymentFailure }
  | { kind: 'ambiguous-failure'; status?: string; failure?: PaymentFailure };

interface PaymentAttempt {
  run(): Promise<TransportResult<PayCardOutput>>;
  cancel(): Promise<void>;
  invalidate(): void;
  currentIntent(): PersistedActiveIntent | null;
}

type ReaderSend =
  | { kind: 'sent'; answer: PaymentIntentDto }
  | { kind: 'refused'; failure: PaymentFailure }
  // A cancel or a newer payment replaced the attempt during the wait.
  | { kind: 'replaced' };

interface PaymentAttemptInput {
  input: PayCardInput;
  readerId: string;
  runId: number;
  paymentAccount: Required<PaymentAccountContext>;
}

const DEFAULT_POLL_INTERVAL_MS = 1_000;
export const CARD_WINDOW_MS = 90_000;
// A status read that Stripe cannot answer gives 503 or 429 at once. A count
// of failed reads ended a payment after a Stripe problem of some seconds, thus
// the attempt ends only after this time with no status answer.
const DEFAULT_STATUS_SILENCE_MS = 15_000;
const CARD_WINDOW_ENDED = Symbol('card window ended');
// The backend accepts a declaration of 1 to 100 copies.
const MAX_DECLARED_COPIES = 100;
const MAX_REMEMBERED_INTENTS = 100;
const MAX_CREATES_AFTER_RELEASE = 5;
const READER_WITHOUT_ACTION_CODES: ReadonlySet<string> = new Set([
  UNKNOWN_READER_CODE,
  READER_OFFLINE_CODE,
]);

// A Set keeps the insertion order, thus the first value is the oldest.
function addBounded(ids: Set<string>, id: string): void {
  ids.add(id);
  if (ids.size <= MAX_REMEMBERED_INTENTS) return;
  const oldest = ids.values().next().value;
  if (oldest !== undefined) ids.delete(oldest);
}

function failedPayment(
  failure?: PaymentFailure
): TransportResult<PayCardOutput> {
  return failure
    ? { ok: false, reason: 'network-error', failure }
    : { ok: false, reason: 'network-error' };
}

// A status request that has no answer at the end of the window must not keep
// the visitor on the card screen. A late answer goes nowhere.
function beforeWindowEnds<T>(
  request: Promise<T>,
  remainingMs: number
): Promise<T | typeof CARD_WINDOW_ENDED> {
  request.catch(() => undefined);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const windowEnds = new Promise<typeof CARD_WINDOW_ENDED>((resolve) => {
    timer = setTimeout(() => resolve(CARD_WINDOW_ENDED), remainingMs);
  });
  return Promise.race([request, windowEnds]).finally(() => clearTimeout(timer));
}

// The backend refuses a declaration whose amounts do not add up to the
// payment. Each declared copy carries the payment intent, thus an amount of 0
// (a free copy, without the intent) must not be in it. O(n) in the copies.
function validDeclaration(input: PayCardInput): number[] | null {
  const amounts = input.copyAmountsCents;
  if (!amounts || amounts.length === 0) return null;
  if (amounts.length > MAX_DECLARED_COPIES) return null;
  if (!amounts.every((amount) => Number.isInteger(amount) && amount > 0)) {
    return null;
  }
  const sum = amounts.reduce((total, amount) => total + amount, 0);
  return sum === input.quote.amountDueCents ? [...amounts] : null;
}

export function createTerminalPaymentModule(
  deps: TerminalPaymentDeps,
  options: TerminalPaymentOptions = {}
): EntrPaymentTransport {
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const cardWindowMs = options.cardWindowMs ?? CARD_WINDOW_MS;
  const statusSilenceMs = options.statusSilenceMs ?? DEFAULT_STATUS_SILENCE_MS;
  const now = deps.now ?? Date.now;
  let activeIntent: PersistedActiveIntent | null = null;
  let activeAttempt: PaymentAttempt | null = null;
  // The cancel of the last intent on the reader also resets a reader after
  // the attempt ended, for example at the walk-away of the visitor.
  let lastReaderIntent: PersistedActiveIntent | null = null;
  let activeRunId = 0;
  // The run of the last send to the reader. Each send waits for the sends in
  // flight, thus the reset of a late send comes before the next send.
  let lastSendRunId = 0;
  const readerSendsInFlight = new Set<Promise<void>>();
  const pollAbortResolvers = new Set<() => void>();
  // Payment intents that cannot take a card again: the backend cancelled
  // them, or the kiosk gave up their held amount at the capture stage. The
  // create of the same order returns such an intent again (the same Stripe
  // idempotency key), and the kiosk never keeps such an intent again.
  const releasedIntentIds = new Set<string>();
  // Intents whose release the backend refused, because the hold step had
  // accepted the hold. The intent holds the payment of the visitor.
  const acceptedAtRelease = new Set<string>();
  // Each close of the card screen in progress. The next payment waits for
  // it, because the close can wait for the answer of a hold step.
  const paymentEndsInFlight = new Set<Promise<void>>();
  const cancelRegistry: ReaderCancelRegistry = createReaderCancelRegistry({
    async cancelPaymentOnReader(intent, options) {
      const answer = await deps.cancelPaymentOnReader(intent, options);
      const status = answer?.paymentIntentStatus ?? '';
      if (isTerminalFailure(status)) {
        rememberReleasedIntent(intent.paymentIntentId);
      }
      if (
        options.releaseHold &&
        (isCaptureReady(status) || isTerminalSuccess(status))
      ) {
        addBounded(acceptedAtRelease, intent.paymentIntentId);
      }
    },
    now,
    wait: deps.wait,
    observer: deps.observer,
  });
  const captureReconciler = createCaptureReconciler({
    checkPaymentIntentStatus: deps.checkPaymentIntentStatus,
    capturePaymentIntent: deps.capturePaymentIntent,
    holdPaymentIntent: deps.holdPaymentIntent,
    preserveIntentIfCurrent,
    clearIntentEverywhere,
    rememberCanceledIntent: rememberReleasedIntent,
    observer: deps.observer,
  });
  const cancelIntent = cancelRegistry.cancelIntent;
  const waitForPendingCancels = cancelRegistry.waitForPendingCancels;
  const reconcileCaptureIntent = captureReconciler.reconcileCaptureIntent;

  // The visitor does not wait for this reset. The registry keeps it in its
  // pending cancels, thus the next send waits until it is complete: a cancel
  // after that send stops the new reader action. A run that a cancel or a
  // newer payment replaced does not reset: the reader can have the newer action.
  function resetReaderAfter(
    runId: number,
    intent: PersistedActiveIntent,
    end: string
  ): void {
    if (activeRunId !== runId) return;
    void cancelIntent(
      intent,
      `reader reset after ${end}`,
      `reader reset after ${end} failed`
    );
  }

  // A send without an answer can be on the reader, or it can have stopped at
  // a proxy during a deploy. Only a reader with no action of this intent and
  // an intent that took no card show that the send did not get to the reader.
  // An action of a different intent in progress is not cleared here.
  async function sendDidNotReachReader(
    intent: PersistedActiveIntent
  ): Promise<boolean> {
    if (!deps.readReaderAction) return false;
    let read: ReaderActionRead | null;
    try {
      read = await deps.readReaderAction({
        paymentIntentId: intent.paymentIntentId,
        readerId: intent.readerId,
        sessionId: intent.sessionId,
        partnerStripeId: intent.partnerStripeId ?? null,
      });
    } catch {
      return false;
    }
    if (!read) return false;
    if (read.actionPaymentIntent === intent.paymentIntentId) return false;
    if (read.actionPaymentIntent && read.actionStatus === 'in_progress') {
      return false;
    }
    return read.intent.status === 'requires_payment_method';
  }

  // A reader that is busy while a cancel of this kiosk can be on it gets one
  // more send in the same try: that cancel ends in some seconds, and the
  // visitor must not lose a try for it. The reader gets no reset, because a
  // reset is the action that makes it busy.
  //
  // A send without an answer is sent again only when the reader shows that
  // the send did not get to it. If Stripe refuses that second send as busy,
  // the first send got to the reader late, and its action is the payment.
  async function sendToReader(
    runId: number,
    intent: PersistedActiveIntent,
    createdIntent: PaymentIntentDto
  ): Promise<ReaderSend> {
    let sentAgainAfterNoAnswer = false;
    for (let send = 1; ; send += 1) {
      const cancelsBeforeSend = cancelRegistry.startedCancels();
      cancelRegistry.forgetIntent(intent);
      lastReaderIntent = intent;
      lastSendRunId = runId;
      try {
        const answer = await deps.sendPaymentIntentToReader({
          paymentIntentId: intent.paymentIntentId,
          readerId: intent.readerId,
          sessionId: intent.sessionId,
          partnerStripeId: intent.partnerStripeId ?? null,
        });
        return { kind: 'sent', answer };
      } catch (error) {
        if (sentAgainAfterNoAnswer && isRefusedWith(error, READER_BUSY_CODE)) {
          deps.observer?.recordEvent(
            'entr.payment',
            'reader busy with the send that had no answer',
            {
              // Full Stripe IDs are intentional telemetry join keys.
              payment_intent_id: intent.paymentIntentId,
            }
          );
          return { kind: 'sent', answer: createdIntent };
        }
        if (
          send === 1 &&
          isRefusedWith(error, READER_BUSY_CODE) &&
          cancelRegistry.mayBeCancelling(cancelsBeforeSend)
        ) {
          deps.observer?.recordEvent(
            'entr.payment',
            'reader busy with a cancel of the kiosk',
            {
              // Full Stripe IDs are intentional telemetry join keys.
              payment_intent_id: intent.paymentIntentId,
            }
          );
          await cancelRegistry.waitForCancelsToEnd();
          if (!isRunActive(runId, intent)) return { kind: 'replaced' };
          continue;
        }
        if (error instanceof PaymentRefusedError) {
          return { kind: 'refused', failure: error.failure };
        }
        if (!(error instanceof RequestOutcomeUnknownError)) throw error;
        deps.observer?.recordEvent(
          'entr.payment',
          'reader send has no answer',
          {
            // Full Stripe IDs are intentional telemetry join keys.
            payment_intent_id: intent.paymentIntentId,
            error:
              error.cause instanceof Error
                ? error.cause.message
                : String(error.cause),
          }
        );
        if (!sentAgainAfterNoAnswer && (await sendDidNotReachReader(intent))) {
          if (!isRunActive(runId, intent)) return { kind: 'replaced' };
          deps.observer?.recordEvent(
            'entr.payment',
            'reader has no action of the send, sending again',
            {
              // Full Stripe IDs are intentional telemetry join keys.
              payment_intent_id: intent.paymentIntentId,
            }
          );
          sentAgainAfterNoAnswer = true;
          continue;
        }
        return { kind: 'sent', answer: createdIntent };
      }
    }
  }

  // The backend can do a send after the reset of a closed card screen. Then
  // the reader shows the amount of that payment, and Stripe refuses the next
  // send as terminal_reader_busy. Thus the next send waits for each send in
  // flight, and a late send of a replaced run resets the reader before it.
  async function sendAndResolveStale(
    runId: number,
    intent: PersistedActiveIntent,
    createdIntent: PaymentIntentDto
  ): Promise<ReaderSend> {
    const step = (async (): Promise<ReaderSend> => {
      const sent = await sendToReader(runId, intent, createdIntent);
      if (sent.kind !== 'sent' || isRunActive(runId, intent)) return sent;
      await resolveStalePostSendIntent(runId, intent, sent.answer);
      return { kind: 'replaced' };
    })();
    const settled = step.then(
      () => undefined,
      () => undefined
    );
    readerSendsInFlight.add(settled);
    void settled.then(() => readerSendsInFlight.delete(settled));
    return step;
  }

  async function waitForReaderToBeFree(): Promise<void> {
    for (;;) {
      if (readerSendsInFlight.size > 0) {
        await Promise.all(readerSendsInFlight);
        continue;
      }
      await waitForPendingCancels();
      if (readerSendsInFlight.size === 0) return;
    }
  }

  // A refused send ends this card interaction, and the visitor sees the cause
  // at once. A reader that Stripe does not know, or that is offline, has no
  // action of this send to reset. A reset of an offline reader also takes
  // approximately 7.3 s and fails, and the next send must wait for it.
  function refusedAtSend(
    runId: number,
    intent: PersistedActiveIntent,
    failure: PaymentFailure
  ): TransportResult<PayCardOutput> {
    deps.observer?.recordEvent('entr.payment', 'reader send refused', {
      code: failure.code,
      failure_category: failure.category,
    });
    if (!isRunActive(runId, intent)) {
      return { ok: false, reason: 'network-error' };
    }
    clearActiveIntent(runId, intent);
    if (READER_WITHOUT_ACTION_CODES.has(failure.code)) {
      if (sameIntent(lastReaderIntent, intent)) lastReaderIntent = null;
    } else {
      resetReaderAfter(runId, intent, 'refused send');
    }
    return failedPayment(failure);
  }

  const orphan = deps.activeIntentStore.read();
  if (orphan) {
    void recoverOrphanIntent(orphan);
  }

  async function recoverOrphanIntent(
    intent: PersistedActiveIntent
  ): Promise<void> {
    if (intent.stage === 'capture') {
      // No release: the same visitor can accept the hold after the reload.
      void cancelIntent(
        intent,
        'reader reset after reload',
        'reader reset after reload failed'
      );
      await reconcileCaptureIntent(intent, false);
      return;
    }

    const cancelled = await cancelIntent(
      intent,
      'recovered orphan payment intent',
      'orphan payment recovery failed'
    );
    if (cancelled) clearIntentEverywhere(intent);
  }

  function isRunActive(runId: number, intent: PersistedActiveIntent): boolean {
    return activeRunId === runId && sameIntent(activeIntent, intent);
  }

  function clearActiveIntent(
    runId: number,
    intent: PersistedActiveIntent
  ): void {
    if (!isRunActive(runId, intent)) return;
    activeIntent = null;
    clearStoredIntentIfCurrent(intent);
  }

  function clearIntentEverywhere(intent: PersistedActiveIntent): void {
    if (sameIntent(activeIntent, intent)) activeIntent = null;
    clearStoredIntentIfCurrent(intent);
  }

  function preserveActiveIntent(
    runId: number,
    intent: PersistedActiveIntent
  ): void {
    if (!isRunActive(runId, intent)) return;
    activeIntent = intent;
    deps.activeIntentStore.write(intent);
  }

  function preserveIntent(intent: PersistedActiveIntent): void {
    activeIntent = intent;
    deps.activeIntentStore.write(intent);
  }

  function preserveIntentIfCurrent(intent: PersistedActiveIntent): void {
    // A late answer of a request for a released intent must not bring it back.
    if (releasedIntentIds.has(intent.paymentIntentId)) return;
    const storedIntent = deps.activeIntentStore.read();
    if (activeIntent && !sameIntent(activeIntent, intent)) return;
    if (storedIntent && !sameIntent(storedIntent, intent)) return;
    preserveIntent(intent);
  }

  function preserveRecoverableIntentIfCurrent(
    intent: PersistedActiveIntent
  ): void {
    const storedIntent = deps.activeIntentStore.read();
    if (
      !sameIntent(activeIntent, intent) &&
      !sameIntent(storedIntent, intent)
    ) {
      return;
    }
    preserveIntent(intent);
  }

  // The reset of a late send is safe until a newer run sends, because that
  // send waits for this resolution. Each write of the intent checks for
  // itself that no newer intent is current.
  function canResolveStaleIntent(runId: number): boolean {
    return lastSendRunId <= runId;
  }

  function clearStoredIntentIfCurrent(intent: PersistedActiveIntent): void {
    if (!sameIntent(deps.activeIntentStore.read(), intent)) return;
    deps.activeIntentStore.clear();
  }

  function rememberReleasedIntent(paymentIntentId: string): void {
    addBounded(releasedIntentIds, paymentIntentId);
  }

  // The same order gives the same idempotency key, thus the create returns an
  // intent that a cancel released or that the backend cancelled. Then the
  // create runs again with a key that names that intent, and Stripe gives a
  // new intent for it.
  async function createIntentThatTakesACard(
    input: PayCardInput,
    partnerStripeId: string | null,
    copyAmountsCents: number[] | null
  ): Promise<PaymentIntentDto> {
    let idempotencyKey = input.idempotencyKey;
    let created: PaymentIntentDto | null = null;
    for (let create = 0; create < MAX_CREATES_AFTER_RELEASE; create += 1) {
      created = await deps.createPaymentIntent({
        amount: input.quote.amountDueCents,
        currency: input.quote.currency.toLowerCase(),
        type: 'terminal',
        idempotencyKey,
        sessionId: input.sessionId,
        partnerStripeId,
        checkoutKey: configuredString(input.checkoutKey) ?? undefined,
        ...(copyAmountsCents ? { copyAmountsCents } : {}),
      });
      if (
        !releasedIntentIds.has(created.id) &&
        !isTerminalFailure(created.status)
      ) {
        return created;
      }
      deps.observer?.recordEvent(
        'entr.payment',
        'create returned a released payment intent',
        {
          // Full Stripe IDs are intentional telemetry join keys.
          payment_intent_id: created.id,
          status: created.status,
        }
      );
      idempotencyKey = `${input.idempotencyKey}-after-${created.id}`;
    }
    if (!created) throw new Error('payment intent create did not run');
    return created;
  }

  function declarationOf(input: PayCardInput): number[] | null {
    const declaration = validDeclaration(input);
    if (!declaration && input.copyAmountsCents?.length) {
      // The kiosk then takes the full amount at the payment, as before.
      deps.observer?.recordEvent(
        'entr.payment',
        'copy amounts do not agree with the payment',
        {
          amount_cents: input.quote.amountDueCents,
          copies: input.copyAmountsCents.length,
          copy_amounts_sum_cents: input.copyAmountsCents.reduce(
            (total, amount) => total + amount,
            0
          ),
        }
      );
    }
    return declaration;
  }

  function wakePollingWaits(): void {
    for (const resolve of pollAbortResolvers) resolve();
    pollAbortResolvers.clear();
  }

  async function waitForPollDelay(
    runId: number,
    delayMs: number
  ): Promise<boolean> {
    if (activeRunId !== runId) return false;

    let abortWait!: () => void;
    const aborted = new Promise<false>((resolve) => {
      abortWait = () => resolve(false);
      pollAbortResolvers.add(abortWait);
    });

    try {
      const elapsed = await Promise.race([
        deps.wait(delayMs).then(() => true as const),
        aborted,
      ]);
      return elapsed && activeRunId === runId;
    } finally {
      pollAbortResolvers.delete(abortWait);
    }
  }

  async function cancelAmbiguousIntent(
    runId: number,
    intent: PersistedActiveIntent
  ): Promise<void> {
    const cancelled = await cancelIntent(
      intent,
      'cancelled ambiguous payment intent',
      'ambiguous payment cancel failed'
    );
    if (cancelled) {
      clearActiveIntent(runId, intent);
      return;
    }

    preserveActiveIntent(runId, intent);
  }

  // The visitor left before the hold step accepted the held amount, or its
  // answer did not come. The backend releases only a hold that the hold step
  // did not accept, and its sweep settles an accepted one. The same request
  // also resets the reader.
  //
  // The backend refuses the release of a hold that the hold step accepted
  // before the release. The visitor who closed the card screen then keeps
  // that intent (keepAcceptedHold): a new payment of the same checkout pays
  // with it, and the backend refuses each other intent for that checkout
  // with CAPTURE_PI_MISMATCH. A different checkout releases it again, thus
  // a kept intent stops no other payment.
  async function releaseUnacceptedHold(
    intent: PersistedActiveIntent,
    end: string,
    keepAcceptedHold: boolean
  ): Promise<void> {
    rememberReleasedIntent(intent.paymentIntentId);
    clearIntentEverywhere(intent);
    await cancelIntent(
      intent,
      `released the unaccepted hold after ${end}`,
      `release of the unaccepted hold after ${end} failed`,
      { releaseHold: true }
    );
    if (!keepAcceptedHold || !acceptedAtRelease.has(intent.paymentIntentId)) {
      return;
    }
    deps.observer?.recordEvent(
      'entr.payment',
      'kept the hold that the hold step accepted before the release',
      {
        // Full Stripe IDs are intentional telemetry join keys.
        payment_intent_id: intent.paymentIntentId,
      }
    );
    releasedIntentIds.delete(intent.paymentIntentId);
    preserveIntentIfCurrent(withStage(intent, 'capture'));
  }

  // No kiosk request cancels an intent without the hold of a declared order,
  // and the kiosk must not capture the payment of a different checkout.
  // Nobody captures the intent, thus Stripe cancels its authorization at the
  // end of the capture period, with no charge.
  function leaveUncapturedIntent(intent: PersistedActiveIntent): void {
    deps.observer?.recordEvent(
      'entr.payment',
      'left the uncaptured payment intent of a different checkout',
      {
        // Full Stripe IDs are intentional telemetry join keys.
        payment_intent_id: intent.paymentIntentId,
      }
    );
    rememberReleasedIntent(intent.paymentIntentId);
    clearIntentEverywhere(intent);
  }

  // The kiosk keeps a hold that the hold step accepted: the backend captures
  // the printed copies of it, or its sweep settles the order.
  async function endCaptureStage(
    intent: PersistedActiveIntent,
    accepted: boolean
  ): Promise<void> {
    const end = 'the payment screen closed';
    if (!accepted && intent.capture === 'after_print') {
      await releaseUnacceptedHold(intent, end, true);
      return;
    }
    if (!accepted) await reconcileCaptureIntent(intent, false);
    await cancelIntent(
      intent,
      `reader reset after ${end}`,
      `reader reset after ${end} failed`
    );
  }

  async function resolveExistingIntent(
    input: PayCardInput
  ): Promise<TransportResult<PayCardOutput> | null> {
    while (paymentEndsInFlight.size > 0) {
      await Promise.allSettled(paymentEndsInFlight);
    }
    await waitForPendingCancels();

    const existingIntent = activeIntent ?? deps.activeIntentStore.read();
    if (!existingIntent) return null;

    if (existingIntent.stage === 'capture') {
      const matchesCheckout = intentMatchesInput(existingIntent, input);
      const resolution = await reconcileCaptureIntent(
        existingIntent,
        matchesCheckout
      );
      if (resolution.kind === 'settled') {
        if (matchesCheckout) {
          return { ok: true, value: { paymentId: resolution.paymentId } };
        }
        return { ok: false, reason: 'network-error' };
      }
      if (resolution.kind === 'cleared-failure') return null;
      if (!matchesCheckout) {
        if (existingIntent.capture === 'after_print') {
          await releaseUnacceptedHold(existingIntent, 'a new checkout', false);
        } else {
          leaveUncapturedIntent(existingIntent);
        }
        return null;
      }
      return { ok: false, reason: 'network-error' };
    }

    const cancelled = await cancelIntent(
      existingIntent,
      'cancelled existing payment intent before new payment',
      'existing payment cancel failed'
    );
    if (!cancelled) {
      preserveIntent(existingIntent);
      return { ok: false, reason: 'network-error' };
    }

    clearIntentEverywhere(existingIntent);
    return null;
  }

  async function waitForCaptureOrSuccess(
    paymentIntentId: string,
    sessionId: string,
    partnerStripeId: string | null,
    initialIntent: PaymentIntentDto,
    runId: number
  ): Promise<PaymentReadinessResult> {
    // After a card failure the reader ends its action and reads no other
    // card: only a new send to the reader does. Thus the poll stops at the
    // first card failure. But Stripe keeps last_payment_error on the intent
    // until the reader reads a new card, so a failure in the answer of the
    // send is from the last card, also when the outcome is pending. Only a
    // failure of a different attempt is new.
    const failureAtSend = initialIntent.failure ?? null;
    const windowEndsAt = now() + cardWindowMs;
    let currentIntent = initialIntent;
    let lastFailure: PaymentFailure | undefined;
    let hasPaymentStarted = false;
    let consecutiveStatusCheckFailures = 0;
    let lastStatusAnswerAt = now();

    function readinessOf(
      intent: PaymentIntentDto
    ): PaymentReadinessResult | null {
      const { status } = intent;
      if (isCaptureReady(status) || isTerminalSuccess(status)) {
        return { kind: 'ready', intent };
      }
      if (isTerminalFailure(status) || isNonRecoverableFailure(intent)) {
        deps.observer?.recordEvent('entr.payment', 'terminal payment failed', {
          status,
          failure_category: intent.failure?.category ?? null,
        });
        return {
          kind: 'final-failure',
          status,
          failure: intent.failure ?? lastFailure,
        };
      }
      if (!isKnownStatus(status)) {
        deps.observer?.recordEvent('entr.payment', 'unknown payment status', {
          status,
        });
        return { kind: 'ambiguous-failure', status };
      }
      if (!isReaderWaitingStatus(status)) {
        hasPaymentStarted = true;
        return null;
      }
      if (hasPaymentFailure(intent)) {
        const failure = intent.failure ?? undefined;
        if (hasPaymentStarted || !isSameFailure(failure, failureAtSend)) {
          deps.observer?.recordEvent('entr.payment', 'card failed on reader', {
            status,
            failure_category: failure?.category ?? null,
          });
          return { kind: 'final-failure', status, failure };
        }
        lastFailure = failure;
        return null;
      }
      if (hasPaymentStarted) {
        deps.observer?.recordEvent(
          'entr.payment',
          'terminal payment cancelled after card interaction',
          { status }
        );
        return { kind: 'final-failure', status, failure: lastFailure };
      }
      return null;
    }

    for (;;) {
      const readiness = readinessOf(currentIntent);
      if (readiness) return readiness;
      if (now() >= windowEndsAt) break;

      const shouldContinue = await waitForPollDelay(
        runId,
        Math.min(pollIntervalMs, windowEndsAt - now())
      );
      if (!shouldContinue) {
        return { kind: 'ambiguous-failure', status: currentIntent.status };
      }
      const remainingMs = windowEndsAt - now();
      if (remainingMs <= 0) break;

      try {
        const checked = await beforeWindowEnds(
          deps.checkPaymentIntentStatus({
            paymentIntentId,
            sessionId,
            partnerStripeId,
          }),
          remainingMs
        );
        if (checked === CARD_WINDOW_ENDED) break;
        currentIntent = checked;
        consecutiveStatusCheckFailures = 0;
        lastStatusAnswerAt = now();
      } catch (error) {
        // A reader cannot take a card for an intent that Stripe does not know.
        if (isRefusedWith(error, UNKNOWN_INTENT_CODE)) {
          return {
            kind: 'final-failure',
            status: currentIntent.status,
            failure: error.failure,
          };
        }
        consecutiveStatusCheckFailures += 1;
        deps.observer?.recordEvent(
          'entr.payment',
          'payment status check failed',
          {
            // Full Stripe IDs are intentional telemetry join keys.
            payment_intent_id: paymentIntentId,
            consecutive_failures: consecutiveStatusCheckFailures,
            no_answer_ms: now() - lastStatusAnswerAt,
            error: error instanceof Error ? error.message : String(error),
          }
        );
        if (now() - lastStatusAnswerAt >= statusSilenceMs) {
          return { kind: 'ambiguous-failure', status: currentIntent.status };
        }
      }
    }
    // The window ended while the reads failed: the result is not known, and
    // the timeout text would tell the visitor that no card came.
    if (consecutiveStatusCheckFailures > 0) {
      return { kind: 'ambiguous-failure', status: currentIntent.status };
    }

    deps.observer?.recordEvent(
      'entr.payment',
      'payment status polling timed out',
      {
        card_window_ms: cardWindowMs,
      }
    );
    // A failure with the attempt_id of the send is from the card of the last
    // send: nobody presented a new card. A failure with no attempt_id can be
    // from a new card that failed the same way, and it tells the visitor more
    // than a timeout.
    const unknownAttemptFailure = lastFailure?.attempt_id
      ? undefined
      : lastFailure;
    return {
      kind: 'ambiguous-failure',
      status: currentIntent.status,
      failure: unknownAttemptFailure ?? {
        category: 'unknown',
        code: PAYMENT_TIMEOUT_CODE,
      },
    };
  }

  function createPaymentAttempt({
    input,
    readerId,
    runId,
    paymentAccount,
  }: PaymentAttemptInput): PaymentAttempt {
    let intent: PersistedActiveIntent | null = null;
    let invalidated = false;
    let accepted = false;
    // The cancel waits for the hold step in flight: the backend can accept
    // the hold before the release, and it then refuses the release.
    let holdStep: Promise<unknown> | null = null;

    function acceptHeldPayment(
      captureIntent: PersistedActiveIntent
    ): Promise<PaymentIntentDto> {
      const step = captureReconciler
        .acceptPayment(captureIntent)
        .then((settled) => {
          if (isAcceptedPayment(settled, captureIntent.capture)) {
            accepted = true;
          }
          return settled;
        });
      // A capture at the payment has no release, thus no cancel waits for it.
      if (captureIntent.capture === 'after_print') {
        holdStep = step.catch(() => undefined);
      }
      return step;
    }

    return {
      async run() {
        try {
          const createdIntent = await createIntentThatTakesACard(
            input,
            paymentAccount.partnerStripeId,
            declarationOf(input)
          );
          // An older backend sends no mode: it captures at the payment.
          const capture: CaptureMode =
            createdIntent.capture === 'after_print'
              ? 'after_print'
              : 'at_payment';

          intent = {
            paymentIntentId: createdIntent.id,
            readerId,
            idempotencyKey: input.idempotencyKey,
            sessionId: input.sessionId,
            stage: 'reader',
            amountCents: input.quote.amountDueCents,
            currency: input.quote.currency.toLowerCase(),
            checkoutKey: configuredString(input.checkoutKey) ?? undefined,
            paymentDestination: paymentAccount.paymentDestination,
            stripeAccountId: paymentAccount.stripeAccountId,
            partnerStripeId: paymentAccount.partnerStripeId,
            capture,
          };

          if (invalidated || activeRunId !== runId) {
            deps.observer?.recordEvent(
              'entr.payment',
              'discarded stale payment intent before reader send',
              {
                // Full Stripe IDs are intentional telemetry join keys.
                payment_intent_id: intent.paymentIntentId,
                reader_id: intent.readerId,
              }
            );
            return { ok: false, reason: 'network-error' };
          }

          activeIntent = intent;
          deps.activeIntentStore.write(intent);
          await waitForReaderToBeFree();
          if (!isRunActive(runId, intent)) {
            return { ok: false, reason: 'network-error' };
          }

          const sent = await sendAndResolveStale(runId, intent, createdIntent);
          if (sent.kind === 'replaced') {
            return { ok: false, reason: 'network-error' };
          }
          if (sent.kind === 'refused') {
            return refusedAtSend(runId, intent, sent.failure);
          }
          const sendResponse = sent.answer;

          const readyIntent = await waitForCaptureOrSuccess(
            createdIntent.id,
            input.sessionId,
            intent.partnerStripeId ?? null,
            sendResponse,
            runId
          );
          if (!isRunActive(runId, intent)) {
            await waitForPendingCancels();
            return { ok: false, reason: 'network-error' };
          }
          if (readyIntent.kind === 'final-failure') {
            if (isTerminalFailure(readyIntent.status)) {
              rememberReleasedIntent(intent.paymentIntentId);
            }
            clearActiveIntent(runId, intent);
            resetReaderAfter(runId, intent, 'card failure');
            return failedPayment(readyIntent.failure);
          }
          if (readyIntent.kind === 'ambiguous-failure') {
            await cancelAmbiguousIntent(runId, intent);
            return failedPayment(readyIntent.failure);
          }

          intent = withStage(intent, 'capture');
          preserveActiveIntent(runId, intent);
          // The card paid: a later reset of the reader for this intent must
          // not release the hold.
          lastReaderIntent = intent;

          // A held amount of the poll is not accepted yet: only the answer
          // of the hold step accepts it.
          const settledIntent = isTerminalSuccess(readyIntent.intent.status)
            ? readyIntent.intent
            : await acceptHeldPayment(intent);
          resetReaderAfter(runId, intent, 'payment');

          if (isTerminalFailure(settledIntent.status)) {
            rememberReleasedIntent(intent.paymentIntentId);
            clearActiveIntent(runId, intent);
            return failedPayment(settledIntent.failure ?? undefined);
          }
          if (!isAcceptedPayment(settledIntent, intent.capture)) {
            preserveActiveIntent(runId, intent);
            return { ok: false, reason: 'network-error' };
          }
          accepted = true;

          deps.observer?.recordEvent(
            'entr.payment',
            'Stripe terminal payment settled',
            {
              // Full Stripe IDs are intentional telemetry join keys.
              payment_intent_id: settledIntent.id,
            }
          );
          clearActiveIntent(runId, intent);

          const output: PayCardOutput = { paymentId: settledIntent.id };
          return { ok: true, value: output };
        } catch (error) {
          const refusal = error instanceof PaymentRefusedError ? error : null;
          // A refusal before the intent exists is an expected answer, not a
          // fault, and there is no intent to cancel.
          if (refusal && !intent) {
            deps.observer?.recordEvent('entr.payment', 'payment refused', {
              code: refusal.failure.code,
            });
            return failedPayment(refusal.failure);
          }
          if (refusal) {
            deps.observer?.recordEvent('entr.payment', 'payment refused', {
              code: refusal.failure.code,
              stage: intent?.stage ?? null,
            });
          } else {
            deps.observer?.recordException(error, 'stripe_terminal_payment');
          }
          deps.observer?.recordEvent(
            'entr.payment',
            'Stripe terminal payment failed',
            {
              error: error instanceof Error ? error.message : String(error),
            }
          );
          // Stripe does not know this intent, thus it holds no money of the
          // visitor. The next try makes a new intent.
          if (
            intent?.stage === 'capture' &&
            isRefusedWith(error, UNKNOWN_INTENT_CODE)
          ) {
            rememberReleasedIntent(intent.paymentIntentId);
            clearIntentEverywhere(intent);
            resetReaderAfter(runId, intent, 'payment');
            return failedPayment(error.failure);
          }
          if (intent?.stage === 'capture') {
            const resolution = await reconcileCaptureIntent(intent, false);
            resetReaderAfter(runId, intent, 'payment');
            if (resolution.kind === 'settled') {
              accepted = true;
              return {
                ok: true,
                value: { paymentId: resolution.paymentId },
              };
            }
          } else if (intent) {
            await cancelAmbiguousIntent(runId, intent);
          }
          return failedPayment(refusal?.failure);
        }
      },

      async cancel() {
        invalidated = true;
        if (holdStep) await holdStep;
        if (!intent) return;

        if (intent.stage === 'capture') {
          await endCaptureStage(intent, accepted);
          return;
        }

        const cancelled = await cancelIntent(
          intent,
          'cancelled active payment intent',
          'Cancel payment on reader failed'
        );
        if (cancelled) {
          clearIntentEverywhere(intent);
          return;
        }

        preserveRecoverableIntentIfCurrent(intent);
      },

      invalidate() {
        invalidated = true;
      },

      currentIntent() {
        return intent;
      },
    };
  }

  return {
    async payCard(input: PayCardInput) {
      const readerId = deps.resolveReaderId();
      if (!readerId) {
        deps.observer?.recordEvent('entr.payment', 'missing Stripe reader id');
        return failedPayment(MISSING_READER_FAILURE);
      }

      const existingResult = await resolveExistingIntent(input);
      if (existingResult) return existingResult;

      deps.observer?.recordEvent(
        'entr.payment',
        'Stripe terminal payment requested',
        {
          amount_cents: input.quote.amountDueCents,
          currency: input.quote.currency,
          reader_id: readerId,
        }
      );

      const runId = activeRunId + 1;
      activeRunId = runId;
      const paymentAccount = resolvePaymentAccount(
        deps.resolvePaymentAccount?.()
      );
      const attempt = createPaymentAttempt({
        input,
        readerId,
        runId,
        paymentAccount,
      });
      activeAttempt = attempt;

      try {
        return await attempt.run();
      } finally {
        if (activeAttempt === attempt) activeAttempt = null;
      }
    },

    async cancelActivePayment() {
      const end = endActivePayment();
      paymentEndsInFlight.add(end);
      try {
        await end;
      } finally {
        paymentEndsInFlight.delete(end);
      }
    },
  };

  async function endActivePayment(): Promise<void> {
    const attempt = activeAttempt;
    const intent =
      attempt?.currentIntent() ?? activeIntent ?? deps.activeIntentStore.read();
    activeRunId += 1;
    attempt?.invalidate();
    wakePollingWaits();

    if (!intent) {
      // The registry sends no second cancel when the reset at the end of
      // the attempt is already done.
      if (lastReaderIntent) {
        await cancelIntent(
          lastReaderIntent,
          'reader reset after the payment screen closed',
          'reader reset after the payment screen closed failed'
        );
      }
      return;
    }

    if (attempt && sameIntent(attempt.currentIntent(), intent)) {
      await attempt.cancel();
      return;
    }

    // A kept intent is not accepted, or the backend refused its release
    // because the hold step accepted it. The release keeps such an intent
    // again (releaseUnacceptedHold).
    if (intent.stage === 'capture') {
      await endCaptureStage(intent, false);
      return;
    }

    const cancelled = await cancelIntent(
      intent,
      'cancelled active payment intent',
      'Cancel payment on reader failed'
    );
    if (cancelled) {
      clearIntentEverywhere(intent);
      return;
    }

    preserveRecoverableIntentIfCurrent(intent);
  }

  async function resolveStalePostSendIntent(
    runId: number,
    intent: PersistedActiveIntent,
    sendResponse: PaymentIntentDto
  ): Promise<void> {
    deps.observer?.recordEvent(
      'entr.payment',
      'resolving stale payment intent after reader send',
      {
        // Full Stripe IDs are intentional telemetry join keys.
        payment_intent_id: intent.paymentIntentId,
        status: sendResponse.status,
      }
    );

    if (!canResolveStaleIntent(runId)) {
      deps.observer?.recordEvent(
        'entr.payment',
        'skipped stale post-send resolution because a newer send started',
        {
          // Full Stripe IDs are intentional telemetry join keys.
          payment_intent_id: intent.paymentIntentId,
        }
      );
      return;
    }

    if (
      isCaptureReady(sendResponse.status) ||
      isTerminalSuccess(sendResponse.status)
    ) {
      const captureIntent = withStage(intent, 'capture');
      preserveRecoverableIntentIfCurrent(captureIntent);
      await reconcileCaptureIntent(captureIntent, false);
      return;
    }

    preserveIntentIfCurrent(intent);
    const cancelled = await cancelIntent(
      intent,
      'cancelled stale post-send payment intent',
      'stale post-send payment cancel failed',
      { force: true }
    );
    if (cancelled) {
      clearIntentEverywhere(intent);
      return;
    }

    preserveIntentIfCurrent(intent);
  }
}
